import type {IntBoolean} from '@myparcel/sdk';
import type {
  GetShipmentsByIdData,
  GetShipmentsData,
  ShipmentResponsesShipments,
} from '@myparcel-frontend/core-api-service';
import type {GetShipmentsParameters} from '@myparcel-frontend/sdk';
import type {Shipment, ValueOf} from '@myparcel-frontend/types';
import {useCoreApiService} from '@myparcel-frontend/core-api-service';
import {useSdk, Status} from '@myparcel-frontend/sdk';
import {attemptAsync} from 'es-toolkit';
import {storeToRefs} from 'pinia';
import {ref} from 'vue';
import {useShipmentStore} from '@/store/shipmentStore/shipmentStore';
import logToMonitor from '@/utils/logging';

type ShipmentsData = ShipmentResponsesShipments['data'];
type CoreShipment = ShipmentsData['shipments'][number];

const status = ref<ValueOf<typeof Status>>(Status.IDLE);

export default function useShipments() {
  const {sdk} = useSdk();
  const shipmentStore = useShipmentStore();
  const {shipments, results} = storeToRefs(shipmentStore);
  const {setShipments, setShipmentHidden} = shipmentStore;
  const {getShipments: getCoreApiShipments, getShipmentsById: getCoreApiShipmentsById} = useCoreApiService();

  const fetchShipments = async (params: GetShipmentsData['query']): Promise<ShipmentsData> => {
    const [error, response] = await attemptAsync(() => getCoreApiShipments(params));

    if (error) {
      logToMonitor(error, {
        _myparcel_extra_info: 'Error fetching shipments',
        _myparcel_filters: JSON.stringify(params),
      });
      throw error;
    }

    return response;
  };

  /**
   * Fetch the shipment list and keep it in the store so single-shipment mutations
   * (e.g. hiding) can be reflected in place, without refetching the whole list.
   * We do want to fetch the list on every get, so we have the most up-to-date state.
   */
  const getShipments = async (params: GetShipmentsData['query']): Promise<ShipmentsData> => {
    const response = await fetchShipments(params);

    setShipments(response);

    return response;
  };

  const getShipmentsById = async (
    shipmentIds: string[],
    query?: GetShipmentsByIdData['query'],
  ): Promise<ShipmentsData> => {
    const [error, response] = await attemptAsync(() => getCoreApiShipmentsById(shipmentIds, query));

    if (error) {
      logToMonitor(error, {
        _myparcel_extra_info: 'Error fetching shipments by ID',
        _myparcel_filters: JSON.stringify(query),
      });
      throw error;
    }

    return response;
  };

  const getShipmentByExternalIdentifier = async ({
    externalIdentifier,
    includeSecondaryShipments = 0,
  }: {
    externalIdentifier: string;
    includeSecondaryShipments?: IntBoolean;
  }): Promise<CoreShipment | undefined> => {
    let result: CoreShipment[];
    try {
      status.value = Status.PENDING;

      /**
       * This error does not need to be logged to NewRelic.
       *
       * The error is thrown when the `external_identifier` is an empty string,
       * which allows the return type of `getShipmentByExternalIdentifier` to only be of one type.
       */
      if (!externalIdentifier) {
        throw new Error("'external_identifier' parameter argument is undefined or empty");
      }

      const params: GetShipmentsParameters = {
        page: 1,
        size: 1,
        external_identifier: externalIdentifier,
      };

      if (includeSecondaryShipments) {
        Object.assign(params, {
          include_secondary_shipments: includeSecondaryShipments,
        });
      }

      const {shipments} = await fetchShipments(params);

      result = shipments;

      status.value = Status.RESOLVED;
    } catch (error: unknown) {
      status.value = Status.REJECTED;
      throw error;
    }

    return result[0];
  };

  const getShipmentByBarcode = async (barcode: string): Promise<CoreShipment | undefined> => {
    let result: CoreShipment[];

    try {
      status.value = Status.PENDING;

      if (!barcode) {
        throw new Error("'barcode' parameter argument is undefined or empty");
      }

      const {shipments} = await fetchShipments({
        barcode,
      });

      result = shipments;

      status.value = Status.RESOLVED;
    } catch (error: unknown) {
      status.value = Status.REJECTED;
      throw error;
    }

    return result[0];
  };

  /**
   * Mark a shipment as delivered by patching its status via the core API.
   * Converts the shipmentId from string to number as required by the core API.
   * @TODO https://internal.jira.dmp.zone/browse/OFF-2833: Remove the need for this function once the core-api-service supports the PATCH /shipments endpoint.
   */
  const markAsDelivered = async (shipmentId: string) => {
    return attemptAsync(() => sdk.patchShipments({body: [{id: Number(shipmentId), delivered: 1}]}));
  };

  const setShipmentsHidden = async (shipmentsToHide: Shipment[], hidden: IntBoolean) => {
    const [error, response] = await attemptAsync(() =>
      sdk.patchShipments({body: shipmentsToHide.map((shipment) => ({id: shipment.id, hidden}))}),
    );

    if (error) {
      logToMonitor(error, {
        _myparcel_extra_info: `Error setting shipment hidden to ${hidden}`,
        _myparcel_ids: JSON.stringify(shipmentsToHide.map((shipment) => shipment.id)),
      });
      throw error;
    }

    shipmentsToHide.forEach((shipment) => setShipmentHidden(shipment.id, hidden));

    return response;
  };

  return {
    shipments,
    results,
    getShipments,
    getShipmentsById,
    getShipmentByBarcode,
    getShipmentByExternalIdentifier,
    markAsDelivered,
    setShipmentsHidden,
    status,
  };
}
